import React, { useEffect, useState } from 'react';
import { Loader2, Video, ShieldCheck, Database, Cloud, ArrowLeft } from 'lucide-react';
import { classroomApi } from '@/lib/classroomApi';
import { toast } from 'sonner';
import Seo from '@/components/shared/Seo';

const Row = ({ icon: Icon, label, value, hint, tone = 'gray' }) => (
  <div className="flex items-start gap-3 py-4 border-b border-gray-100 last:border-0">
    <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${tone === 'green' ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-50 text-gray-700'}`}>
      <Icon className="h-4 w-4" />
    </div>
    <div className="flex-1">
      <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold">{label}</div>
      <div className="text-sm font-semibold text-gray-900 mt-0.5">{value}</div>
      {hint && <div className="text-xs text-gray-500 mt-0.5">{hint}</div>}
    </div>
  </div>
);

const AdminClassroomSettingsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await classroomApi.adminSettings();
        setData(r);
      } catch (e) {
        if (e.status === 401 || e.status === 403) {
          window.location.assign('/admin/login'); return;
        }
        toast.error(e.message || 'Could not load settings');
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]"><Loader2 className="h-6 w-6 animate-spin text-red-700" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="admin-classroom-settings">
      <Seo title="Classroom Settings · Admin · Winhouse" canonical="/admin/classroom-settings" />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <a href="/admin/classes" className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 mb-4">
          <ArrowLeft className="h-4 w-4" /> Back to classes
        </a>
        <div className="mb-6">
          <div className="text-xs uppercase tracking-[0.2em] text-red-700 font-semibold mb-1.5">Admin</div>
          <h1 className="text-3xl font-bold tracking-tight">Classroom Settings</h1>
          <p className="text-gray-600 text-sm mt-1">Provider, branding and rollout configuration.</p>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-6">
          <Row icon={Video} label="Video provider"
               value={data?.video_provider?.toUpperCase()}
               hint={data?.provider_ready ? 'Live video active' : 'Stub placeholder — set DAILY_API_KEY in backend/.env and CLASSROOM_VIDEO_PROVIDER=daily to activate'}
               tone={data?.provider_ready ? 'green' : 'gray'} />
          <Row icon={ShieldCheck} label="Access control"
               value="One-time tokenized join links"
               hint="Per-booking tutor and student tokens. Admin generates and shares manually in Phase 1." />
          <Row icon={Cloud} label="Resource drive"
               value={data?.resource_storage?.toUpperCase() || 'STUB'}
               hint={data?.resource_storage_ready ? 'Cloudinary signed uploads live — files saved to winhouse/classroom/{booking_id}' : 'Add Cloudinary credentials to enable real file storage.'}
               tone={data?.resource_storage_ready ? 'green' : 'gray'} />
          <Row icon={Database} label="Booking creation"
               value="Admin-created only"
               hint="Self-serve student booking lands in Phase 2." />
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-6">
          <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-3">Phase 2 roadmap</div>
          <ul className="text-sm text-gray-700 space-y-2 list-disc list-inside">
            <li>Wire Daily.co API key — room creation + per-participant meeting tokens</li>
            <li>Cloud recordings with admin approval gate</li>
            <li>Collaborative whiteboard (tldraw or excalidraw)</li>
            <li>Cloudinary / S3 upload for the class drive</li>
            <li>Tutor login (Google) for self-serve session management</li>
            <li>Paystack payment gate before booking confirmation</li>
            <li>Interactive quizzes + polls</li>
            <li>Tutor payouts via Paystack subaccounts</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdminClassroomSettingsPage;
